import React, { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { CopyButton } from '@/components/buttons/CopyButton'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import WidgetContext from '@/components/widget/widget.context'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { revokeStreamedCsvUrl } from '@/reducers/scorecards/scorecards.actions'

export const ExportsTable = () => {
  const { handleChangeCsvData } = useContext(WidgetContext)
  const { data, loading, filters } = useSelector((state) => state.scorecards)
  const dispatch = useDispatch()

  // An object URL pins its Blob for the life of the document, and an export
  // Blob can be hundreds of MB. Preview clicks revoke the previous one; this
  // covers navigating away with an export still held.
  useEffect(() => () => dispatch(revokeStreamedCsvUrl()), [dispatch])

  useEffect(() => {
    if (data.tableRows?.advancedExports?.length > 0) {
      data.tableRows.advancedExports.forEach((row) => {
        // Advanced table doesn't like zero values. It ignores them. Set them to a string as workaround.
        // TODO: can just format toString() in the columns instead of this
        Object.keys(row).forEach((key) => {
          if (row[key] === 0) {
            row[key] = '0'
          }
        })

        const rowIsCallSummary =
          (row.call_summary && !row.call_summary.value) || (row.Summary && !row.Summary.value)

        if (rowIsCallSummary) {
          const summaryData = row.call_summary || row.Summary
          row.call_summary = {
            as: (
              <div>
                <InteractiveTooltip interactive content={summaryData} theme="light">
                  <div>{summaryData ? summaryData.slice(0, 50) : ''}...</div>
                </InteractiveTooltip>
              </div>
            ),
            value: summaryData,
          }
          row.Summary = row.call_summary
        }

        // we get data from 2 endpoints, one has call_id and the other one has Call Id, so we need to check both
        // similar how the summary is done with call_summary and Summary
        const callId = row['Call Id'] || row.call_id

        if (callId) {
          const callIdValue = callId.value || callId

          row.call_id = {
            as: (
              <div className="flex small-gap">
                <Link
                  to={`/calls/${callIdValue}`}
                  data-testid={`exports-call-${callIdValue}`}
                  className="table-link"
                >
                  {callIdValue}
                </Link>
                <CopyButton content={callIdValue} showTooltip iconOnly />
              </div>
            ),
            value: callIdValue,
          }
          row['Call Id'] = row.call_id
        }
      })
    }
  }, [data.tableRows?.advancedExports])

  useEffect(() => {
    // The visible table renders the 20-row preview (advancedExports). The CSV
    // download instead serves the streamed export Blob as-is, via its object
    // URL — the server already produced the CSV, so there is nothing to parse
    // or rebuild. Aggregate paths don't stream, so they still fall back to
    // serialising the preview slot.
    const streamedCsvUrl = data.advancedExportsCsvUrl

    if (streamedCsvUrl) {
      handleChangeCsvData({
        blobUrl: streamedCsvUrl,
        fileName: 'Scorecard Exports',
        filters: { startDate: filters.startDate, endDate: filters.endDate },
      })
      return
    }

    // A failed stream must not quietly fall through to the preview: handing
    // back 20 rows with no error is exactly what made every backend failure
    // look like a hard 20-row cap to customers (CUS-154 / CUS-220).
    if (data.advancedExportsCsvFailed) {
      handleChangeCsvData({})
      return
    }

    const updatedData = data.tableRows.advancedExports.map((row) => ({
      ...row,
      call_summary: row.call_summary && row.call_summary.value,
      Summary: row.Summary && row.Summary.value,
      call_id: row.call_id?.value || row.call_id,
      'Call Id': row['Call Id']?.value || row['Call Id'],
    }))

    handleChangeCsvData({
      headers: data.tableHeaders?.advancedExports.filter((column) => !column.is_hidden),
      data: updatedData,
      fileName: 'Scorecard Exports',
      filters: { startDate: filters.startDate, endDate: filters.endDate },
    })
    // `filters` is deliberately absent from the deps: the filename must
    // describe the export that actually ran, not whatever the date pickers
    // show now. A new Preview revokes the URL and re-runs this effect, so a
    // genuinely new export always picks up the filters it was run with.
  }, [
    data.tableRows.advancedExports,
    data.tableHeaders?.advancedExports,
    data.advancedExportsCsvUrl,
    data.advancedExportsCsvFailed,
  ])

  return (
    <div data-testid="scorecard-exports__table" className="scorecard-exports__table">
      <AdvancedTable
        index="uuid"
        rows={data.tableRows?.advancedExports}
        columns={data.tableHeaders?.advancedExports.filter((column) => !column.is_hidden)}
        loading={loading.scores}
        loadingText="Your data for this report is running. This could take a few minutes..."
        defaultOrderBy={data.tableHeaders?.advancedExports[0]?.accessor}
        defaultOrder="asc"
        wrapColumnContent={false}
        pagination
      />
    </div>
  )
}
